import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Divider, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ReactElement } from 'react';

export interface CustomItemRenderProps {
    val: any;
    setVal: (updated: any) => void;
    itemsSelection?: string[];
}

export const LogicalItem = {
    OR: 'OR',
    AND: 'AND',
};

export interface MappingItemProps {
    name: string;
    displayName?: string;
    description?: string;
    mergeFrom?: string;
    label?: string;
    value?: any;
    isRequired: boolean;
    type: 'int' | 'bool' | 'string' | 'select' | 'custom' | 'free_text';
    labelValueDependent?: boolean;
    dependencies?: { dependencyValues: string[]; logicalContext: string };
    options?: any[];
    displayProperty?: string;
    hasEmptyItem?: boolean;
    featureFlag?: { name: string; trueValue: boolean };
    render?: any;
    allowMultiple?: boolean;
    category?: string;
    isInvisible?: boolean;
    valueSelectedObserved?: boolean;
    valueSelectedObservedFilter?: any;
    tooltip?: string;
    observerOn?: string;
}

export interface ConfigValueType {
    Int: 'int';
    Bool: 'bool';
    String: 'string';
    Select: 'select';
    Custom: 'custom';
    FreeText: 'free_text';
}

export interface MappingItemWidgetProps extends MappingItemProps {
    onReject: () => void;
    renderDropdown?: (cb: (path: string[]) => void) => ReactElement;
    setLabel: (l: string) => void;
    clearLabel: () => void;
    isLabelDup: (l: string) => boolean;
    deleteCustomField: (fieldName: string) => void;
}

const colorMap = {
    Mapped: '#8FDBAB',
    OptionalUnmapped: '#818183',
    Unmapped: '#ED4D4D',
};

const MappingItem = (props: MappingItemWidgetProps) => {
    const { onReject, renderDropdown, deleteCustomField, ...item } = props;

    const getColorMap = (item: MappingItemProps) => {
        const isItemMapped = checkIfItemMapped(item);

        if (isItemMapped) {
            return colorMap.Mapped;
        }
        if (item.isRequired && !isItemMapped) {
            return colorMap.Unmapped;
        }
        return colorMap.OptionalUnmapped;
    };

    const checkIfItemMapped = (item: MappingItemProps) => {
        if (
            (item.value && !item.labelValueDependent) ||
            (item.value && item.labelValueDependent && item.label !== '')
        ) {
            return true;
        }

        return false;
    };

    const getLabelErrorText = (item: MappingItemProps) => {
        if (props.isLabelDup(item.label)) {
            return 'This label already exists';
        }
        if (item.labelValueDependent && item.label === '' && item.value !== '') {
            return 'Please add custom label to field';
        }
        return '';
    };

    const isLabelValid = (item: MappingItemProps) => {
        if (props.isLabelDup(item.label)) {
            return true;
        }
        if (item.labelValueDependent && item.label === '' && item.value !== '') {
            return true;
        }

        return false;
    };

    const getHeirarchyInfo = (item: MappingItemProps) => `${item.name} level can only be mapped along with 
        ${item.dependencies?.dependencyValues.join(item.dependencies?.logicalContext === LogicalItem.AND ? ' and ' : ' or ')}. 
        please make sure you've mapped the fields mentioned if you want to assign value to this field`;

    const renderTooltip = (item: MappingItemProps) => {
        let info = item.tooltip;
        if (item.dependencies && item.value !== '') {
            info = [info, getHeirarchyInfo(item)].join('\n');
        }
        return info ? (
            <Tooltip title={info} placement="right" arrow>
                <InfoOutlinedIcon fontSize="small" color="primary" />
            </Tooltip>
        ) : undefined;
    };

    return (
        <Stack
            className="MappingItem"
            height={120}
            borderRadius={1}
            data-testid={`data-settings-mapping-field-div-${item.name}`}
        >
            <Box className="ItemStateIndicator" sx={{ background: getColorMap(item) }} />

            <Stack direction="column" className="ItemDetails">
                <Stack className={item.category !== 'custom' ? 'ItemDetailsRow1' : 'CustomItemDetailsRow1'} spacing={1}>
                    <Stack direction="row">
                        <Typography className="ItemTitle" variant="body2" color={getColorMap(item)}>
                            {' '}
                            {checkIfItemMapped(item) ? 'Mapped' : 'Unmapped'}
                        </Typography>
                        <Typography className="ItemTitle" variant="body2" color={getColorMap(item)}>
                            {' '}
                            |{' '}
                        </Typography>
                        <Typography className="ItemDisplayName" variant="body2">
                            {' '}
                            {item.displayName || item.name}{' '}
                        </Typography>
                        {item.isRequired ? <Typography color={colorMap.Unmapped}>*</Typography> : null}
                        <Typography align="center" className="ItemTooltip">
                            {renderTooltip(item)}
                        </Typography>
                    </Stack>
                    {item.category !== 'custom' && (
                        <Stack direction="column">
                            <Typography variant="body1" className="DescriptionContainer">
                                {' '}
                                {item.description}
                            </Typography>
                            <Divider />
                        </Stack>
                    )}
                </Stack>

                {item.category !== 'custom' && (
                    <Stack direction="row" className="ItemDetailsRow">
                        <Stack direction="column" className="ItemDropDownSettingsContainer">
                            {((item.label === '' && !item.labelValueDependent) ||
                                (item.label === '' && !item.isRequired && item.value === '')) && (
                                <Button
                                    className="ItemDropDownSettingsLabelAction"
                                    data-testid={`data-settings-mapping-customize-btn-${item.name}`}
                                    startIcon={<AddCircleOutlineIcon />}
                                    onClick={() => {
                                        if (props.setLabel) {
                                            props.setLabel(item.name);
                                        }
                                    }}
                                >
                                    Customize Label
                                </Button>
                            )}
                            {item.label !== '' ||
                            (item.labelValueDependent && item.isRequired) ||
                            (item.value !== '' && item.labelValueDependent) ? (
                                <Stack direction="row" className="CustomLabelContainer">
                                    <Typography className="CustomLabelText">Label</Typography>
                                    <TextField
                                        error={isLabelValid(item)}
                                        placeholder="Enter custom label"
                                        helperText={getLabelErrorText(item)}
                                        className="ItemTextField"
                                        variant="outlined"
                                        size="small"
                                        inputProps={{
                                            'data-testid': `data-settings-mapping-customize-textfield-${item.name}`,
                                        }}
                                        value={item.label}
                                        onChange={(e) => {
                                            props.setLabel(e.target.value);
                                        }}
                                    />
                                    {!item.labelValueDependent && (
                                        <IconButton
                                            onClick={() => {
                                                if (props.clearLabel) {
                                                    props.clearLabel();
                                                }
                                            }}
                                            className="ClearLabelIcon"
                                            edge="end"
                                        >
                                            <ClearIcon color="inherit" fontSize="inherit" />
                                        </IconButton>
                                    )}
                                </Stack>
                            ) : null}
                        </Stack>
                        {
                            <div className="ItemDropdownContainer">
                                {renderDropdown ? renderDropdown((path) => {}) : null}
                                {!item.isRequired && (
                                    <IconButton
                                        onClick={() => {
                                            onReject();
                                        }}
                                        className="ClearItemIcon"
                                        edge="end"
                                    >
                                        <ClearIcon color="inherit" fontSize="inherit" />
                                    </IconButton>
                                )}
                            </div>
                        }
                    </Stack>
                )}
            </Stack>
            {item.category === 'custom' && (
                <IconButton onClick={() => deleteCustomField(item.name)}>
                    <DeleteIcon />
                </IconButton>
            )}
        </Stack>
    );
};

export default MappingItem;
